import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Custom hook for browser-based voice input using Web Speech API
 * @param {Object} options
 * @param {Function} options.getCurrentValue - Function to get current textarea value
 * @param {Function} options.handleTranscriptUpdate - Callback when transcript updates (newValue)
 * @param {number} [options.maxLength=4000] - Maximum length for the transcript
 * @returns {Object} { isListening, isSpeechSupported, handleVoiceToggle }
 */
export const useVoiceInput = ({ getCurrentValue, handleTranscriptUpdate, maxLength = 4000 }) => {
  const recognitionRef = useRef(null)
  const baseValueRef = useRef('')
  const interimAccumulatorRef = useRef('')
  const [isListening, setIsListening] = useState(false)
  const [isSpeechSupported, setIsSpeechSupported] = useState(false)

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window === 'undefined') return

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return

    setIsSpeechSupported(true)
    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      // Each event contains ALL results from the start of recognition
      // Helper to add text with proper spacing
      const appendWithSpace = (text, addition) => {
        if (!addition) return text
        return text + (text && !text.endsWith(' ') ? ' ' : '') + addition
      }

      // Separate final and interim results
      const finalParts = []
      const interimParts = []

      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.trim()
        if (!transcript) continue

        if (event.results[i].isFinal) {
          finalParts.push(transcript)
        } else {
          interimParts.push(transcript)
        }
      }

      // Build final transcript from all final parts
      const finalTranscript = finalParts.join(' ')

      // Update interim accumulator: reset if last result was final, otherwise accumulate
      const lastResultIsFinal = event.results.length > 0 && event.results[event.results.length - 1].isFinal
      if (lastResultIsFinal) {
        interimAccumulatorRef.current = ''
      } else if (interimParts.length > 0) {
        // Accumulate interim fragments across events
        interimAccumulatorRef.current = interimParts.join(' ')
      }

      // Build complete value: base + final + interim
      let newValue = baseValueRef.current
      newValue = appendWithSpace(newValue, finalTranscript)
      newValue = appendWithSpace(newValue, interimAccumulatorRef.current)
      newValue = newValue.slice(0, maxLength)

      handleTranscriptUpdate(newValue)
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsListening(false)
      if (event.error === 'no-speech') {
        // Auto-stop if no speech detected - this is normal
        recognition.stop()
      }
    }

    recognition.onend = () => {
      setIsListening(false)
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [handleTranscriptUpdate, maxLength])

  const handleVoiceToggle = useCallback(() => {
    if (!recognitionRef.current) return

    if (isListening) {
      recognitionRef.current.stop()
      setIsListening(false)
      interimAccumulatorRef.current = ''
    } else {
      try {
        // Capture current value as base - preserves any text typed before voice input
        baseValueRef.current = getCurrentValue() || ''
        interimAccumulatorRef.current = ''
        recognitionRef.current.start()
        setIsListening(true)
      } catch (error) {
        console.error('Failed to start speech recognition:', error)
        setIsListening(false)
      }
    }
  }, [isListening, getCurrentValue])

  return {
    isListening,
    isSpeechSupported,
    handleVoiceToggle
  }
}
